<template>
  <div :class="labelShowInlineStyle" data-test="raft-autocomplete-wrapper">
    <slot name="border">
      <v-border-style :status="borderStatus" />
    </slot>
    <slot name="radiogroup"></slot>
    <label
      :for="elementId"
      :class="fieldLabelClasses"
      v-if="showLabel"
      :id="elementId + '-label'"
      >{{ label }}: <sup v-if="fieldRequired">*</sup></label
    >
    <div
      :id="elementId"
      :aria-owns="resultsElementId"
      class="autocomplete"
      role="combobox"
      aria-haspopup="listbox"
      :aria-expanded="showResults"
    >
      <slot name="input"></slot>
      <div class="search-input-row">
        <div
          class="autocomplete__box"
          :class="{
            autocomplete__searching: showResults,
            invalid: invalidBorder,
          }"
        >
          <div class="autocomplete__inputs">
            <input
              v-model="display"
              :placeholder="placeholder"
              :disabled="disabled"
              :maxlength="maxlength"
              :class="inputClass"
              :name="elementId"
              :required="fieldRequired"
              @click="search"
              @input="search"
              @keydown.enter.prevent="enter"
              @keydown.tab="close"
              @keydown.up="up"
              @keydown.down="down"
              @keydown.esc="close"
              @focus="focus"
              @blur="blur"
              type="text"
              autocomplete="off"
              role="searchbox"
              aria-autocomplete="list"
              :aria-controls="resultsElementId"
              :aria-activedescendant="activeDescendant"
              :aria-labelledby="elementId + '-label'"
            />
            <input :name="name" type="hidden" :value="value" />
          </div>
        </div>
        <!-- Close -->
        <filter-clear-button
          v-if="value || display"
          @click="resetAutocomplete"
        />
      </div>
      <!-- Choose from these matches or search unlinked chemicals -->
      <span><slot name="helptext" /></span>
      <ul
        v-show="showResults && !hideResults"
        :id="resultsElementId"
        :class="resultsShowInlineStyle"
        :style="listStyle"
        role="listbox"
        aria-label="Autocomplete Results"
      >
        <slot name="results">
          <!-- results -->
          <template>
            <slot name="firstResult" />
            <template v-for="(result, index) in results">
              <li
                :key="'li' + '-' + result[resultsValue || 'id'] + '-' + index"
              >
                <label :key="'label-' + result[resultsValue || 'id']">
                  <input
                    type="radio"
                    :key="'radio-' + result[resultsValue || 'id']"
                    @click="selectAndClose(result)"
                    class="autocomplete__results__item"
                    :class="{ autocomplete__selected: isSelected(index) }"
                    :aria-selected="isSelected(index)"
                    :id="getId(index)"
                    :checked="isSelected(index)"
                  />
                  {{ formatDisplay(result) || result.text }}
                </label>
              </li>
            </template>
            <slot name="lastResult" />
          </template>

          <!-- no results -->
          <li
            v-if="noResultMessage"
            class="autocomplete__results__item autocomplete__no-results"
          >
            <slot name="noResults"> No Results. </slot>
          </li>
        </slot>
      </ul>
    </div>
    <div v-if="hasError" class="error">
      {{ error }}
    </div>
    <slot name="action" />
  </div>
</template>

<script>
import checkmark from "@/images/check.svg";
import trash from "@/images/trash_filled.svg";
import FilterClearButton from "@/components/filters/FilterClearButton.vue";
import { useDrupalSettings } from "@/composables/useDrupalSettings";
import throbberActive from "@/images/throbber-active.gif";
import throbberInactive from "@/images/throbber-inactive.png";
import VBorderStyle from "./VBorderStyle.vue";

export default {
  name: "RaftAutocomplete",
  components: {
    FilterClearButton,
    VBorderStyle,
  },
  props: {
    label: {
      type: String,
      default: null,
    },
    elementId: {
      type: String,
      required: true,
    },
    source: {
      type: Function,
      required: true,
    },
    placeholder: {
      default: "Search",
      type: String,
    },
    inputClass: {
      type: [String, Object],
      default: null,
    },
    name: {
      type: String,
      default: "",
    },
    resultsProperty: {
      type: String,
      default: "",
    },
    resultsValue: {
      type: String,
      default: "id",
    },
    resultsDisplay: {
      type: [String, Function],
      default: "name",
    },
    resultsFormatter: {
      type: Function,
      default: null,
    },
    showNoResults: {
      type: Boolean,
      default: false,
    },
    maxlength: {
      type: Number,
      default: 50,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showLabel: {
      type: Boolean,
      default: true,
    },
    defaultValue: {
      type: [Object, String],
      default: null,
    },
    fieldRequired: {
      type: Boolean,
      default: false,
    },
    resultsShowInline: {
      type: Boolean,
      default: false,
    },
    isChemsnapEnabled: {
      type: Boolean,
      default: false,
    },
    chemsnapFound: {
      type: String,
      default: "",
    },
    borderStatus: {
      type: String,
      default: "",
    },
    hideResults: {
      type: Boolean,
      default: false,
    },
    labelShowInline: {
      type: Boolean,
      default: false,
    },
    invalidBorder: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const config = useDrupalSettings();
    return { config };
  },
  data() {
    return {
      value: null,
      display: null,
      results: [],
      selectedIndex: null,
      loading: false,
      isFocused: false,
      error: null,
      selectedId: null,
      selectedDisplay: null,
      eventListener: false,
      activeDescendant: "",
      checkSrc: checkmark,
      trashSrc: trash,
      open: !!this.resultsShowInline,
      showSearchInput: false,
      throbberActiveSrc: throbberActive,
      throbberInactiveSrc: throbberInactive,
      pickFirst: false,
    };
  },
  computed: {
    resultsElementId: function () {
      return this.elementId + "-results";
    },
    showResults() {
      return (
        (Array.isArray(this.results) && this.results.length > 0 && this.open) ||
        this.hasError
      );
    },
    noResults() {
      return Array.isArray(this.results) && this.results.length === 0;
    },
    noResultMessage() {
      return (
        this.noResults &&
        !this.isLoading &&
        this.isFocused &&
        this.showNoResults
      );
    },
    isEmpty() {
      return !this.display;
    },
    isLoading() {
      return this.loading === true;
    },
    hasError() {
      return this.error !== null;
    },
    listStyle() {
      if (this.isLoading) {
        return {
          color: "#ccc",
        };
      }
      return "";
    },
    fieldLabelClasses() {
      if (this.fieldRequired) {
        return "field-label field__label required";
      }
      return "field-label field__label";
    },
    /*
     * Check if a text has been entered, but no option selected on submit.
     *
     * @return {boolean} Returns true for valid/empty, false if there are any errors.
     */
    validOptionSelected() {
      if (this.isEmpty) {
        return true;
      }
      return !!(this.selectedId || this.selectedDisplay);
    },
    resultsShowInlineStyle() {
      return this.resultsShowInline
        ? "autocomplete__results-inline"
        : "autocomplete__results-absolute";
    },
    showChemsnapBadge() {
      return this.isChemsnapEnabled && this.showResults;
    },
    labelShowInlineStyle() {
      return this.labelShowInline
        ? "raft-autocomplete-wrapper-inline"
        : "raft-autocomplete-wrapper";
    },
  },
  methods: {
    search: function () {
      this.$emit("search", this.display);
      this.results = [];
      this.selectedIndex = null;
      this.error = null;
      if (typeof this.source === "function") {
        // No resource search with no input.
        if (!this.display || this.display.length < 1) {
          return;
        }

        this.loading = true;
        this.setEventListener();
        return this.source(this.display, this.setResults);
      }

      throw new TypeError("invalid source");
    },
    /**
     * Set results property from api response.
     *
     * @param {Object|Array} response
     *   Object or array of objects representing the search results.
     *
     * @return {Array}
     *   Returns an array of strings representing the results to display for selection.
     */
    setResults: function (response) {
      this.results = response;
      this.emitRequestResultEvent();
      this.loading = false;
      this.open = true;

      // Allow other functions to format the results.
      if (this.resultsFormatter) {
        const formatted = this.resultsFormatter(response);
        this.results = formatted;
        if (this.pickFirst) {
          this.selectAndClose(this.results?.[0]);
          this.pickFirst = false;
          this.emitValidationError();
        }
        return formatted;
      }

      if (this.pickFirst) {
        this.selectAndClose(this.results?.[0]);
        this.pickFirst = false;
        this.emitValidationError();
      }
      // If the results is an array of objects, we expect the resultsProperty to be set so that we know which value
      // to show in the selection dropdown.
      if (this.resultsProperty && response[this.resultsProperty]) {
        return response[this.resultsProperty];
      }

      if (Array.isArray(response)) {
        return response;
      }

      return [];
    },

    /**
     * Emit an event based on the request results.
     */
    emitRequestResultEvent: function () {
      if (!this.results || this.results.length === 0) {
        this.$emit("results", { results: this.results });
        this.$emit("noResults", { query: this.display });
      } else {
        this.$emit("results", { results: this.results });
        this.$emit("autocomplete-results", { results: this.results });
        this.showSearchInput = true;
      }
    },
    /**
     * Select a result.
     *
     * @param {Object} obj - Object representing the selected search result.
     */
    select: function (obj) {
      if (!obj) {
        return;
      }

      this.updateValue(obj);

      this.$emit("selected", {
        value: this.value,
        display: this.display,
        selectedObject: obj,
      });
      this.$emit("input", this.value);
      this.selectedIndex = this.results.findIndex(
        (result) =>
          result[this.resultsValue] === obj[this.resultsValue] ||
          result.id === obj.id ||
          result.uid === obj.uid,
      );
      this.setActiveDescendant();
    },
    selectAndClose(obj) {
      this.select(obj);
      this.close();
    },
    updateValue: function (obj) {
      if (!obj) {
        this.value = null;
      } else {
        this.value =
          this.resultsValue && obj[this.resultsValue]
            ? obj[this.resultsValue]
            : obj.id || obj.uid;
      }
      if (this.value) {
        this.display = this.formatDisplay(obj);
        this.selectedDisplay = this.display;
      } else {
        this.display = "";
        this.selectedDisplay = "";
      }
    },

    /**
     * Allow external control over the formatting of a single result in an array of results.
     *
     * This method allows for parent components to specify whether to use a single property in each result object,
     * or a callback function to perform additional transformations.
     *
     * @param  {Object} obj - Object representing an autocomplete result.
     *
     * @return {String} - Returns a string representing the formatted search result.
     */
    formatDisplay: function (obj) {
      switch (typeof this.resultsDisplay) {
        case "function":
          return this.resultsDisplay(obj);

        case "string":
          if (!obj[this.resultsDisplay]) {
            throw new Error(
              `"${this.resultsDisplay}" property expected on result but is not defined.`,
            );
          }
          return obj[this.resultsDisplay];

        default:
          throw new TypeError("missing resultsDisplay prop");
      }
    },

    getId: function (key) {
      return this.elementId + "-result-" + key;
    },

    /**
     * Register the component as focused.
     */
    focus: function () {
      this.isFocused = true;
    },

    /**
     * Remove the focused value.
     */
    blur: function () {
      this.isFocused = false;
    },

    isChecked(obj) {
      const property = this.resultsValue ?? "id";
      const objValue = obj[property]?.[0] || obj[property];
      const valueToCompare = this.value?.[0] || this.value;
      return valueToCompare === objValue;
    },

    /**
     * Determine if a result is selected.
     *
     * @param {Number} key - Number representing the selected index from the search results.
     *
     * @return {Boolean} Returns true if selected, false otherwise.
     */
    isSelected: function (key) {
      return key === this.selectedIndex;
    },

    /**
     * Sets the active descendant to support the aria-activedescendant property (a11y).
     */
    setActiveDescendant() {
      this.activedescendant = this.getId(this.selectedIndex);
    },

    /**
     * Focus on the previous results item.
     */
    up() {
      if (this.selectedIndex === null) {
        this.selectedIndex = this.results.length - 1;
      } else {
        this.selectedIndex =
          this.selectedIndex === 0
            ? this.results.length - 1
            : this.selectedIndex - 1;
      }
      this.setActiveDescendant();
    },

    /**
     * Focus on the next results item.
     */
    down() {
      if (this.selectedIndex === null) {
        this.selectedIndex = 0;
      } else {
        this.selectedIndex =
          this.selectedIndex === this.results.length - 1
            ? 0
            : this.selectedIndex + 1;
      }
      this.setActiveDescendant();
    },

    /**
     * Select an item via the keyboard
     */
    enter() {
      if (this.selectedIndex === null) {
        this.selectedIndex = 0;
      }
      this.selectAndClose(this.results?.[this.selectedIndex]);
      this.$emit("enter", this.display);
    },

    /**
     * Clear all values, results and errors
     */
    resetAutocomplete: function () {
      this.display = null;
      this.value = null;
      this.results = null;
      this.error = null;
      this.$emit("search", "");
      this.$emit("clear");
      this.showSearchInput = false;
      this.emitValidationError();
    },

    /**
     * Close the results list. If nothing was selected clear the search.
     */
    close: function () {
      if (!this.resultsShowInline) {
        this.open = false;
      }
      if (!this.value || !this.selectedDisplay) {
        if (this.results?.length > 0) {
          this.select(this.results?.[this.selectedIndex]);
        } else {
          this.emitValidationError();
        }
      }
      if (this.selectedDisplay !== this.display && this.value) {
        this.display = this.selectedDisplay;
      }

      this.removeEventListener();
      this.$emit("close");
    },

    /**
     * Add event listener for clicks outside the results.
     *
     * @return {boolean} Returns true for success, false otherwise.
     */
    setEventListener() {
      if (this.eventListener) {
        return false;
      }
      this.eventListener = true;
      document.addEventListener("click", this.clickOutsideListener, true);
      return true;
    },

    /**
     * Remove the click event listener
     */
    removeEventListener() {
      this.eventListener = false;
      document.removeEventListener("click", this.clickOutsideListener, true);
    },

    /**
     * Method invoked by the event listener.
     *
     * @param {Object} event - The event to respond to.
     */
    clickOutsideListener(event) {
      if (this.$el && !this.$el.contains(event.target)) {
        this.close();
      }
    },

    /**
     * Method invoked by the parent component to manually update entered value.
     *
     * @param {String} data - String to be displayed and searched.
     */
    updateSearch(data) {
      this.display = data;
      this.search();
    },

    /**
     * Display or clear an error when text has been entered but no value chosen.
     */
    emitValidationError() {
      if (!this.validOptionSelected) {
        this.error = "No match. Please select a valid option.";
      } else {
        this.error = null;
      }
      this.$emit("validation-error", {
        field: this.elementId,
        error: this.error,
      });
    },
  },
  mounted() {
    if (this.chemsnapFound) {
      this.updateValue(this.chemsnapFound);
    }
    this.updateValue(this.defaultValue);
  },
  watch: {
    results: {
      immediate: true,
      handler(newValue) {
        this.emitRequestResultEvent();
        if (newValue?.length > 0) {
          this.selectedIndex = 0;
        }
      },
    },
    defaultValue: {
      immediate: true,
      handler(newValue) {
        if (typeof newValue === "string") {
          this.$set(this, "display", newValue);
          this.search();
          return;
        }
        // Skip calling update value when this was passed recursively instead of a value.
        if (newValue?.value && newValue?.selectedObject && newValue?.display) {
          return;
        }
        this.updateValue(newValue);
      },
    },
  },
};
</script>

<style scoped>
.raft-autocomplete-wrapper div.autocomplete-success {
  font-size: 0.8rem;
  color: green;
}
.raft-autocomplete-wrapper label {
  font-weight: bold;
  grid-column: 2;
}

.raft-autocomplete-wrapper {
  display: grid;
  grid-template-rows: max-content max-content;
  grid-template-columns: 0.25rem 1fr;
  column-gap: 0.25rem;
}
/* 1fr 5.2fr is specific to match AddRecord.vue form */
.raft-autocomplete-wrapper-inline {
  display: grid;
  grid-template-rows: max-content max-content;
  grid-template-columns: 0.25rem 1fr 5.2fr;
  column-gap: 0.25rem;
}

.raft-autocomplete-wrapper-inline label {
  font-weight: bold;
  grid-column: 2;
  align-self: center;
  margin-right: 2.5rem;
}

.raft-autocomplete-wrapper-inline div.autocomplete {
  grid-column: 3;
}

.autocomplete {
  grid-column: 2;
  display: block;
  position: relative;
  width: 100%;
  box-sizing: border-box;
}
.autocomplete__box {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 3px;
  padding: 0 5px;
  width: 100%;
  max-width: 25rem;
}

.autocomplete__searching {
  border-radius: 3px 3px 0 0;
}

.autocomplete__inputs {
  flex-grow: 1;
  padding: 0 5px;
}

.autocomplete__inputs input {
  width: 100%;
  border: 0;
}

.autocomplete__inputs input:focus {
  outline: none;
}

.autocomplete--clear {
  cursor: pointer;
  display: inline-block;
}

/* 1.311 rem is the size of an li search result */
.autocomplete__results-inline {
  margin: 0;
  padding: 0;
  list-style-type: none;
  max-height: calc(1.311rem * 7.5);
  overflow-y: scroll;
  background: white;
  width: 99.5%;
  border: 1px solid var(--gray-400);
  color: var(--gray-900);
}

.autocomplete__results-absolute {
  margin: 0;
  padding: 0;
  list-style-type: none;
  z-index: 1000;
  position: absolute;
  max-height: 400px;
  overflow-y: auto;
  background: white;
  width: 100%;
  border: 1px solid #ccc;
  border-top: 0;
  color: black;
}

.error {
  color: red;
  grid-column: span 2;
}

.autocomplete__selected {
  background: #0072b9;
}

.search-input-row {
  display: flex;
}

.chemsnap-badge {
  margin: 0.25rem 0;
}

:is(#raft-js, #raft-js-app) input {
  font-size: 0.75rem;
}

.search-input-row div.autocomplete__box.invalid {
  border: 2px solid var(--red-900);
  border-radius: 4px;
}
</style>
